/* eslint-disable no-empty-pattern */
import * as React from 'react';
import { GET_CABINET_PLC_DATA, CABINET_SET_REAL_TIME, CABINET_TIME_SLOT_CONTROL, CABINET_FORCE_CONTROL, CABINET_DISABLE_BRANCH, TCabinetEntityPlcData, TGetCabinetDataInput, TGetCabinetPlcDataOutput, TCurrentTimeControlInput, TCabinetSetRealTimeOutput, TCabinetTimeSlotControlOutput, TCabinetForceControlOutput, TTimeSlotControlInput, TControlActionInput } from '../../../../graphql/schema';
import { CabinetContext } from '../../../../context/CabinetContext';
import { useSubscription, useMutation } from '@apollo/client';
import { Button, Form, Input, Switch, TimePicker, message } from 'antd';
import { SendOutlined, SyncOutlined, ToolFilled } from '@ant-design/icons';
import { Dayjs } from 'dayjs';
import { formatTime } from '../../../../utils/commons';

interface IControlDetailProps {
}

// Form type that allows Dayjs objects for time fields
type ControlFormData = Omit<TCabinetEntityPlcData, 'br1OnTime' | 'br1OffTime' | 'br2OnTime' | 'br2OffTime'> & {
  br1OnTime?: Dayjs;
  br1OffTime?: Dayjs;
  br2OnTime?: Dayjs;
  br2OffTime?: Dayjs;
};


const ControlDetail: React.FunctionComponent<IControlDetailProps> = (props) => {
  const [form] = Form.useForm<ControlFormData>();
  const { selectedDevice } = React.useContext(CabinetContext)!
  const [allowSetting, setAllowSetting] = React.useState(false);
  const timeFormat = "HH:mm";

  const [syncTimeMutation] = useMutation<TCabinetSetRealTimeOutput, TCurrentTimeControlInput>(CABINET_SET_REAL_TIME);
  const [setBranchTimeMutation] = useMutation<TCabinetTimeSlotControlOutput, TTimeSlotControlInput>(CABINET_TIME_SLOT_CONTROL);
  const [forceControlMutation] = useMutation<TCabinetForceControlOutput, TControlActionInput>(CABINET_FORCE_CONTROL);
  const [enableBranchMutation] = useMutation(CABINET_DISABLE_BRANCH);
  const { loading } = useSubscription<TGetCabinetPlcDataOutput, TGetCabinetDataInput>(GET_CABINET_PLC_DATA, {
    variables: {
      cabinetDeviceId: selectedDevice!.id
    },
    skip: !selectedDevice?.id,
    onData(d) {
      // The subscription data structure is different from queries
      // It should be d.data?.getCabinetPlcData, not d.data.data?.getCabinetPlcData
      const result = (d.data.data as any)?.getCabinetPlcData
      if (result) {
        form.setFieldsValue({
          ...result,
          // rtcTime: new Date(result.rtcTime).toLocaleString('en-US', { timeZone: 'Asia/Bangkok' }),
          br1OnTime: formatTime(result.br1OnTime.toString()),
          br1OffTime: formatTime(result.br1OffTime.toString()),
          br2OnTime: formatTime(result.br2OnTime.toString()),
          br2OffTime: formatTime(result.br2OffTime.toString()),
        });
      }
    },
    onError(error) {
      console.error("Subscription error:", error);
      message.error(`Lỗi kết nối dữ liệu thời gian thực: ${error.message}`);
    }
  });

  const handleSyncTimePLC = () => {
    if (!selectedDevice) {
      message.error("Không tìm thấy thiết bị!");
      return;
    }

    const currentDate = new Date(new Date().getTime() + 7 * 60 * 60 * 1000);
    syncTimeMutation({
      variables: {
        input: {
          deviceId: selectedDevice.id,
          date: currentDate
        }
      }
    })
      .then(() => {
        message.success("Đồng bộ thời gian thành công!");
      })
      .catch((err) => {
        message.error(`Lỗi đồng bộ thời gian: ${err.message}`);
      });
  };

  const handleSetBranchTime = () => {
    if (!selectedDevice) {
      message.error("Không tìm thấy thiết bị!");
      return;
    }

    // Get form values
    const formValues = form.getFieldsValue();
    const { br1OnTime, br1OffTime, br2OnTime, br2OffTime } = formValues;

    // Validate that all time fields are filled
    if (!br1OnTime || !br1OffTime || !br2OnTime || !br2OffTime) {
      message.error("Vui lòng nhập đầy đủ thời gian cho tất cả nhánh!");
      return;
    }

    // Format times to HHMM format (e.g., "1705" for 17:05)
    const formatTimeToString = (time: Dayjs) => {
      return time.format('HHmm');
    };

    setBranchTimeMutation({
      variables: {
        input: {
          deviceId: selectedDevice.id,
          branch1TimeSlot: {
            onTime: formatTimeToString(br1OnTime),
            offTime: formatTimeToString(br1OffTime)
          },
          branch2TimeSlot: {
            onTime: formatTimeToString(br2OnTime),
            offTime: formatTimeToString(br2OffTime)
          }
        }
      }
    })
      .then(() => {
        message.success("Cài đặt thời gian nhánh thành công!");
      })
      .catch((err) => {
        message.error(`Lỗi cài đặt thời gian nhánh: ${err.message}`);
      });
  };

  const handleForceStateChange = (checked: boolean) => {
    if (!selectedDevice) {
      message.error("Không tìm thấy thiết bị!");
      return;
    }

    forceControlMutation({
      variables: {
        input: {
          deviceId: selectedDevice.id,
          action: checked
        }
      }
    })
      .then(() => {
        message.success(`Cưỡng chế ${checked ? 'bật' : 'tắt'} tủ thành công!`);
      })
      .catch((err) => {
        message.error(`Lỗi cưỡng chế ${checked ? 'bật' : 'tắt'} tủ: ${err.message}`);
      });
  };

  const handleEnableBranch = (branchNumber: number, enabled: boolean) => {
    if (!selectedDevice) {
      message.error("Không tìm thấy thiết bị!");
      return;
    }

    enableBranchMutation({
      variables: {
        input: {
          deviceId: selectedDevice.id,
          branch: branchNumber,
          status: enabled ? 0 : 1
        }
      }
    })
      .then(() => {
        message.success(`Nhánh ${branchNumber} đã được ${enabled ? 'bật' : 'tắt'} thành công!`);
      })
      .catch((err) => {
        message.error(`Lỗi ${enabled ? 'bật' : 'tắt'} nhánh ${branchNumber}: ${err.message}`);
      });
  };

  return <>
    <Form
      form={form}
      name="basic"
      wrapperCol={{ span: 24 }}
      className={`grid gap-2 md:mx-0 ${loading ? "opacity-45" : ""}`}
      // onFinish={onFinishControl}
      autoComplete="off"
    >
      <div className='flex flex-col gap-2'>
        <div className="flex flex-row gap-2">
          <div className="flex items-center w-[200px]">
            Cho phép cài đặt/điều khiển:
          </div>
          <div className="flex items-center col-span-1">
            <Switch
              defaultChecked={false}
              checked={allowSetting}
              onChange={(checked) => setAllowSetting(checked)}
            />
          </div>
          {/* TODO: Add reset device button */}
          {/* {allowSetting && (
                        <div className="flex items-center col-span-4 gap-2">
                            <Button
                            // onClick={() => {
                            //     const d = deviceData;
                            //     d && form.setFieldsValue({
                            //         ...d,
                            //         br1OnTime: formatTime(d.br1OnTime),
                            //         br1OffTime: formatTime(d.br1OffTime),
                            //         br2OnTime: formatTime(d.br2OnTime),
                            //         br2OffTime: formatTime(d.br2OffTime),
                            //         // hps_ontime: formatTime(d.hps_ontime),
                            //         // hps_offtime: formatTime(d.hps_offtime),
                            //     });
                            // }}
                            >
                                Hủy
                            </Button>
                            <Button
                                className="bg-blue-primary text-variant-white"
                                htmlType="submit"
                            >
                                Xác nhận
                            </Button>
                        </div>
                    )} */}
        </div>
        {/* PLC time */}
        <div className="flex gap-2 flex-row">
          <Form.Item
            className="w-full h-0"
            label="Thời gian của tủ"
            name="rtcTime"
          >
            <Input disabled readOnly />
          </Form.Item>
          <Button
            title="Đồng bộ thời gian cho tủ"
            disabled={!allowSetting}
            icon={<SyncOutlined />}
            onClick={handleSyncTimePLC}
          ></Button>
        </div>

        {/* Out, Branch Status */}
        <div className='flex flex-1 items-center flex-row gap-2'>
          <div className="flex items-center col-span-2">Ngõ ra PLC:</div>
          <Form.Item
            className="col-span-1 h-[8px]"
            valuePropName="checked"
            name="out1Status"
          >
            <Switch checkedChildren="N1" unCheckedChildren="N1" disabled />
          </Form.Item>
          <Form.Item
            className="col-span-1 h-[8px]"
            valuePropName="checked"
            name="out2Status"
          >
            <Switch checkedChildren="N2" unCheckedChildren="N2" disabled />
          </Form.Item>
        </div>
        <div className='flex flex-1 items-center flex-row gap-2'>
          <div className="flex items-center col-span-2">
            Trạng thái nhánh đèn:
          </div>
          <Form.Item
            className="col-span-1 h-[8px]"
            valuePropName="checked"
            name="br1Status"
          >
            <Switch checkedChildren="N1" unCheckedChildren="N1" disabled />
          </Form.Item>
          <Form.Item
            className="col-span-1 h-[8px]"
            valuePropName="checked"
            name="br2Status"
          >
            <Switch checkedChildren="N2" unCheckedChildren="N2" disabled />
          </Form.Item>
        </div>
        <div className='flex flex-1 items-center flex-row gap-2'>
          <div className="flex items-center col-span-2 gap-1">
            <ToolFilled className="text-xs mb-2 text-variant-warning" />
            <span>Cho phép hoạt động:</span>
          </div>
          <Form.Item
            className="col-span-1 h-[8px]"
            valuePropName="checked"
            name="!disBr1"
          >
            <Switch
              checkedChildren="N1"
              unCheckedChildren="N1"
              disabled={!allowSetting}
              onChange={(checked) => handleEnableBranch(1, checked)}
            />
          </Form.Item>
          <Form.Item
            className="col-span-1 h-[8px]"
            valuePropName="checked"
            name="!disBr2"
          >
            <Switch
              checkedChildren="N2"
              unCheckedChildren="N2"
              disabled={!allowSetting}
              onChange={(checked) => handleEnableBranch(2, checked)}
            />
          </Form.Item>
        </div>
        <div className='flex flex-1 items-center flex-row gap-2'>
          <div className='flex flex-col gap-2'>
            <div className='flex flex-1 items-center flex-row gap-2'>
              <div className="flex items-center col-span-2 gap-1">
                <ToolFilled className="text-xs mb-2 text-variant-warning" />
                Thời gian mở Nhánh 1:
              </div>
              <Form.Item className="col-span-1 h-[8px]" label="Từ" name="br1OnTime">
                <TimePicker format={timeFormat} disabled={!allowSetting} />
              </Form.Item>
              <Form.Item className="col-span-1 h-[8px]" label="Đến" name="br1OffTime">
                <TimePicker format={timeFormat} disabled={!allowSetting} />
              </Form.Item>
            </div>
            <div className='flex flex-1 items-center flex-row gap-2'>
              <div className="flex items-center col-span-2 gap-1">
                <ToolFilled className="text-xs mb-2 text-variant-warning" />
                Thời gian mở Nhánh 2:
              </div>
              <Form.Item className="col-span-1 h-[8px]" label="Từ" name="br2OnTime">
                <TimePicker format={timeFormat} disabled={!allowSetting} />
              </Form.Item>
              <Form.Item className="col-span-1 h-[8px]" label="Đến" name="br2OffTime">
                <TimePicker format={timeFormat} disabled={!allowSetting} />
              </Form.Item>
            </div>
          </div>
          <Button
            title="Đồng bộ thời gian cho tất cả nhánh"
            disabled={!allowSetting}
            icon={<SendOutlined />}
            onClick={() => handleSetBranchTime()}
          ></Button>
        </div>
        {/* TODO: Add CT Type */}
        <div className='flex flex-1 items-center flex-row gap-2'>
          <div className="flex items-center col-span-2">
            Trạng thái cửa tủ:
          </div>
          <Form.Item
            className="col-span-2 h-[8px]"
            valuePropName="checked"
            name="doorState"
          >
            <Switch
              checkedChildren="Mở"
              unCheckedChildren="Đóng"
              disabled={true}
            />
          </Form.Item>
        </div>

        {/* Force State */}
        <div className='flex flex-1 items-center flex-row gap-2'>
          <div className="flex items-center col-span-2 gap-1">
            Cưỡng chế bật tủ:
          </div>
          <Form.Item
            className="col-span-2 h-[8px]"
            valuePropName="checked"
            name="forceState"
          >
            <Switch disabled={!allowSetting} onChange={handleForceStateChange} />
          </Form.Item>
        </div>
        <div className='flex flex-1 items-center flex-row gap-2 justify-between'>
          <div className='flex flex-col'>
            <div className="flex items-center col-span-2 gap-1">
              <ToolFilled className="text-xs mb-2 text-variant-warning" />
              Thời gian bật:
            </div>
            <Form.Item className="flex flex-col h-[8px]" name="forceTotalTime">
              <Input
                className="w-[136px]"
                suffix={<span>giây</span>}
                disabled={!allowSetting}
              />
            </Form.Item>
          </div>
          <div className='flex flex-col'>
            <div className="flex items-center col-span-2 gap-1">
              Thời gian bật hiện tại:
            </div>
            <Form.Item
              className="flex flex-col h-[8px]"
              name="forceCurrentTime"
            >
              <Input
                className="w-[136px]"
                disabled
                suffix={<span>giây</span>}
              />
            </Form.Item>
          </div>
          <div className='flex flex-col'>
            <div className="flex items-center col-span-2 gap-1">
              Thời gian bật còn lại:
            </div>
            <Form.Item
              className="flex flex-col h-[8px]"
              name="forceRemainTime"
            >
              <Input
                className="w-[136px]"
                disabled
                suffix={<span>giây</span>}
              />
            </Form.Item>
          </div>
        </div>

        {/* Manual State */}
        <div className='flex flex-1 flex-col gap-2'>
          <div className='flex flex-row gap-2'>
            <div className="flex items-center col-span-2">
              Trạng thái công tắc tay:
            </div>
            <Form.Item
              className="col-span-2 h-[8px]"
              valuePropName="checked"
              name="manualState"
            >
              <Switch disabled />
            </Form.Item>
          </div>
          <div className='flex flex-row gap-2 justify-between'>
            <div className='flex flex-col'>
              <div className="flex items-center col-span-2 gap-1">
                <ToolFilled className="text-xs mb-2 text-variant-warning" />
                Thời gian bật:
              </div>
              <Form.Item className="col-span-2 h-[8px]" name="manualTotalTime">
                <Input
                  className="w-[136px]"
                  suffix={<span>giây</span>}
                  disabled={!allowSetting}
                />
              </Form.Item>
            </div>
            <div className='flex flex-col'>
              <div className="flex items-center col-span-2 gap-1">
                Thời gian bật hiện tại:
              </div>
              <Form.Item
                className="col-span-2 h-[8px]"
                name="manualCurrentTime"
              >
                <Input
                  className="w-[136px]"
                  disabled
                  suffix={<span>giây</span>}
                />
              </Form.Item>
            </div>
            <div className='flex flex-col'>
              <div className="flex items-center col-span-2 gap-1">
                Thời gian bật còn lại:
              </div>
              <Form.Item
                className="col-span-2 h-[8px]"
                name="manualRemainTime"
              >
                <Input
                  className="w-[136px]"
                  disabled
                  suffix={<span>giây</span>}
                />
              </Form.Item>
            </div>
          </div>
        </div>
      </div>
    </Form>
  </>;
};

export default ControlDetail;
